import { SlideOver } from 'ui/slideOver';
import { useSelector, useDispatch } from 'react-redux';
import { selectSendActivity } from 'modules/workflowIndex/selectors';
import { useExitRuleContext } from 'modules/workflows/context/ExitRuleContext';
import { useWorkflowContext } from 'modules/workflows/context';
import { getIsJourneyExitEventsEnabled } from 'modules/workflows/ducks';
import { Node } from 'modules/workflows/types';
import { Nest16 } from 'ui/icons';
import { ChildRule, Rule } from 'modules/journey/components/RuleBuilder/types';
import { FocusPosition, focusNode, useFlowChart, DEFAULT_PADDING } from 'ui/workflows';
import { useEffect, useState } from 'react';
import { getNodes, getStartNodeId, getWorkflow, selectNodes } from '../../ducks';
import {
  RuleCriteriaText,
  RuleCriteriaValue,
  SearchQueryCriteriaCardContainer,
  ExitRuleSlideOverContainer,
  RuleCard,
  RuleCardCount,
  RuleCardCriteriaContainer,
  RuleCardHeader,
  RuleCardHeaderWrapper,
  RuleCardWrapper,
  RuleCardsContainer,
  SetOn,
  SliderHeader,
  RuleAggregateCard,
  RuleAggregateCount,
  RuleAggregateHeader,
  TileExitSelect,
  StyledExitRuleClickToCopy,
} from './ExitRuleSlideOver.styled';
import {
  normalizeComparatorType,
  getFormattedCreatedAt,
  getExitRuleCountsForNodeId,
  aggregateExitRuleCountsForJourney,
  getTotalCount,
  isSetOrNotSetComparatorType,
  getSelectedValue,
  ALL_KEY,
  getOptions,
} from './ExitRuleSlideOver.utils';
import { ExitRuleCriteria } from './ExitRuleSlideOverCriteria';
import { RuleTypeEnum } from './ExitRuleSliderOver.constants';

export const EXIT_RULE_SLIDER_OVER_TEST_ID = 'exit-rule-slideover';
export const EXIT_RULE_DATE_TIME_TEST_ID = 'exit-rule-date-time';
export const EVENT_SEARCH_QUERY_CRITERIA_CARD_CONTAINER_TEST_ID =
  'event-search-query-criteria-card-container';
export const PROPERTY_SEARCH_QUERY_CRITERIA_CARD_CONTAINER_TEST_ID =
  'property-search-query-criteria-card-container';
export const RULE_CRITERIA_TEXT_TEST_ID = 'rule-criteria-text';
export const getRuleCardCriteriaTestId = (index: number) => `rule-card-criteria-${index}`;

export const PANEL_WIDTH = 225;
export const REVIEW_PANEL_WIDTH = 400;
const CANVAS_SHIFT_X = (-REVIEW_PANEL_WIDTH + DEFAULT_PADDING) / 2;
const DURATION = 400;

const getExitRuleCardTitle = (type: RuleTypeEnum) => {
  switch (type) {
    case RuleTypeEnum.RuleCombo:
      return 'Users exited';
    case RuleTypeEnum.ConversionMetExitRule:
      return 'Users exited after converting';
    default:
      return 'Users exited by rule';
  }
};

type PropertySearchQueryProps = { comparatorType: string; value: string | number | null };

export const PropertySearchQuery = ({ comparatorType, value }: PropertySearchQueryProps) => {
  return (
    <SearchQueryCriteriaCardContainer
      data-test={PROPERTY_SEARCH_QUERY_CRITERIA_CARD_CONTAINER_TEST_ID}
    >
      <Nest16 />
      {isSetOrNotSetComparatorType(comparatorType) ? (
        <RuleCriteriaText data-test={RULE_CRITERIA_TEXT_TEST_ID}>
          <RuleCriteriaValue>{value}</RuleCriteriaValue> {normalizeComparatorType(comparatorType)}
        </RuleCriteriaText>
      ) : (
        <>
          <RuleCriteriaText data-test={RULE_CRITERIA_TEXT_TEST_ID}>
            {normalizeComparatorType(comparatorType)} <RuleCriteriaValue>{value}</RuleCriteriaValue>
          </RuleCriteriaText>
        </>
      )}
    </SearchQueryCriteriaCardContainer>
  );
};

type EventSearchQueryProps = {
  isFirst: boolean;
  field: string;
  value: string | number | null;
  comparatorType: string;
};

export const EventSearchQuery = ({
  isFirst,
  field,
  value,
  comparatorType,
}: EventSearchQueryProps) => {
  return (
    <SearchQueryCriteriaCardContainer
      data-test={EVENT_SEARCH_QUERY_CRITERIA_CARD_CONTAINER_TEST_ID}
    >
      <Nest16 />
      <div>
        {
          <RuleCriteriaText data-test={RULE_CRITERIA_TEXT_TEST_ID}>
            {isFirst ? 'Where ' : 'And '}
            {isSetOrNotSetComparatorType(comparatorType) ? (
              <>
                <RuleCriteriaValue>{field}</RuleCriteriaValue>{' '}
                {normalizeComparatorType(comparatorType)}
              </>
            ) : (
              <RuleCriteriaValue>{field}</RuleCriteriaValue>
            )}
          </RuleCriteriaText>
        }
        {!isSetOrNotSetComparatorType(comparatorType) && (
          <RuleCriteriaText data-test={RULE_CRITERIA_TEXT_TEST_ID}>
            {normalizeComparatorType(comparatorType)} <RuleCriteriaValue>{value}</RuleCriteriaValue>
          </RuleCriteriaText>
        )}
      </div>
    </SearchQueryCriteriaCardContainer>
  );
};

const ExitRuleCards = ({
  exitRules,
  exitRulesCounts,
}: {
  exitRules: Rule[];
  exitRulesCounts: Record<string, number>;
}) => {
  const isJourneyExitEventsEnabled = useSelector(getIsJourneyExitEventsEnabled);
  return exitRules?.map((rule: Rule, ruleIdx: number) => {
    const { id: exitRuleId } = rule;
    const count = exitRulesCounts[exitRuleId as string];
    const { enabledDate, archivedDate } = rule;
    const exitRuleType = rule.ruleType ?? rule.childRules[0].ruleType;
    const title = getExitRuleCardTitle(exitRuleType as RuleTypeEnum);

    return (
      <RuleCardWrapper data-test={EXIT_RULE_SLIDER_OVER_TEST_ID} key={`exit-rule-${ruleIdx}`}>
        <RuleCard>
          <RuleCardHeaderWrapper>
            {isJourneyExitEventsEnabled && exitRuleId && (
              <>
                Exit rule met
                <StyledExitRuleClickToCopy id={exitRuleId} />
              </>
            )}
            <RuleCardHeader>{title}</RuleCardHeader>
            <RuleCardCount>{count}</RuleCardCount>
          </RuleCardHeaderWrapper>
          {rule?.childRules.map((childRule: ChildRule, childIndex: number) => {
            const { ruleType } = childRule;
            return (
              <RuleCardCriteriaContainer
                key={`exit-rule-child-${childIndex}`}
                data-test={getRuleCardCriteriaTestId(childIndex)}
              >
                <ExitRuleCriteria childRule={childRule} />
                {childRule?.searchQuery?.searchQueries?.map(
                  ({ field = '', comparatorType = '', value = '' }, searchQueryIndex: number) => {
                    return (
                      <div key={`search-query-${searchQueryIndex}`}>
                        {ruleType === RuleTypeEnum.UserPropertyRule ? (
                          <PropertySearchQuery comparatorType={comparatorType} value={value} />
                        ) : (
                          <EventSearchQuery
                            isFirst={searchQueryIndex === 0}
                            field={field}
                            comparatorType={comparatorType}
                            value={value}
                          />
                        )}
                      </div>
                    );
                  },
                )}
              </RuleCardCriteriaContainer>
            );
          })}
        </RuleCard>
        {(enabledDate || archivedDate) && (
          <SetOn data-test={EXIT_RULE_DATE_TIME_TEST_ID}>
            {enabledDate
              ? `Set on ${getFormattedCreatedAt(enabledDate)}`
              : `Removed on ${getFormattedCreatedAt(archivedDate)}`}
          </SetOn>
        )}
      </RuleCardWrapper>
    );
  });
};

export const ExitRuleSlideOver = () => {
  const [firstTimeLoad, setFirstTimeLoad] = useState(true);
  const dispatch = useDispatch();
  const { setShowExitSlideover, showExitRuleSlideOver } = useWorkflowContext();
  const nodes = useSelector(getNodes);
  const startNodeId = useSelector(getStartNodeId);
  const selectedNodes = nodes.filter((node) => node.isSelected === true);
  const selectedNode = selectedNodes?.[0] || {};
  const { graph, zoomTo } = useFlowChart();
  const { id: selectedNodeId } = selectedNode || ({} as Node);

  useEffect(() => {}, []);

  useEffect(() => {
    if (graph) {
      const nodeId = selectedNodeId || startNodeId.toString();
      if (selectedNodeId || !firstTimeLoad) {
        zoomTo(1);
        focusNode(
          graph,
          nodeId,
          FocusPosition.Center,
          true,
          { duration: DURATION },
          { x: CANVAS_SHIFT_X, y: 0 },
        );
      }
    }
    if (firstTimeLoad) {
      setFirstTimeLoad(false);
    }
  }, [selectedNodeId]);

  const onClose = () => setShowExitSlideover(false);

  const { allExitRules: exitRules } = useExitRuleContext();
  const { id: workflowId } = useSelector(getWorkflow);

  const sendActivity = useSelector(selectSendActivity(workflowId));
  const exitRuleExitsByNode = sendActivity?.exitRuleExitsByNode || {};

  const exitRulesCounts = selectedNodeId
    ? getExitRuleCountsForNodeId(exitRuleExitsByNode, selectedNodeId)
    : aggregateExitRuleCountsForJourney(exitRuleExitsByNode);

  const totalExitCount = getTotalCount(
    aggregateExitRuleCountsForJourney(exitRuleExitsByNode),
  ) as number;
  const totalCurrentExitcount = getTotalCount(exitRulesCounts) as number;
  const exitRuleIds = Object.keys(exitRulesCounts || {});
  const nodeIds = Object.keys(exitRuleExitsByNode || {});

  const filteredExitRules = exitRules.filter((rule: Rule) => exitRuleIds.includes(rule?.id || ''));

  const options = getOptions(nodeIds, nodes, exitRuleExitsByNode, totalExitCount);

  const selectedValues = [getSelectedValue(selectedNodeId, nodes)];

  const handleTileSelect = (val: { value: string }[]) => {
    if (val[0]?.value === ALL_KEY) {
      dispatch(selectNodes([]));
    } else {
      const id = val[0]?.value as string;
      dispatch(selectNodes([id]));
    }
  };

  return (
    <SlideOver
      hasOverlay={true}
      bodyContent={
        <ExitRuleSlideOverContainer>
          <RuleCardsContainer>
            <TileExitSelect
              onSelect={handleTileSelect}
              options={options}
              placeholder="Search or select an item..."
              value={selectedValues}
            />
            <RuleAggregateCard>
              <RuleAggregateHeader>Total users exited</RuleAggregateHeader>
              <RuleAggregateCount>{totalCurrentExitcount}</RuleAggregateCount>
            </RuleAggregateCard>
            <ExitRuleCards exitRules={filteredExitRules} exitRulesCounts={exitRulesCounts} />
          </RuleCardsContainer>
        </ExitRuleSlideOverContainer>
      }
      headerContent={<SliderHeader>{'Exit rule breakdown'}</SliderHeader>}
      id="exit-rule-slideover"
      onClickOutside={onClose}
      onClose={onClose}
      isOpen={showExitRuleSlideOver}
    />
  );
};
